
import React, { useCallback, useMemo, useRef, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import Menu from '../../../element/ui/core/menu';
import Transparency from '../../../element/icons/transparency';
import Shadow from '../../../element/icons/shadow';
import Stroke from '../../../element/icons/stroke';
import Position from '../../../element/icons/position';
import { Text as TextIcon } from '../../../element/icons/text';

import { useStore } from '../../../hooks';
import SidebarPanelButton from '../SidebarPanelButton';
import AnnotationOptionPanelHeader from './AnnotationOptionPanelHeader';
import OpacityField from './OpacityField';
import StrokeFields from './StrokeFields';
import ShadowFields from './ShadowFields';
import PositionFields from './PositionFields';
import {
  ANNOTATION_OPTION_POPUP_MIN_WIDTH,
  POSITION_POPUP_MIN_WIDTH,
  StyledOptionPopupContent,
  StyledAnnotationSidebarActions,
  StyledOptions,
} from './AnnotationOptions.styled';
import { POPPABLE_OPTIONS } from './AnnotationOptions.constants';
import ColorInput from '../ColorInput';
import GroupLockControls from '../GroupLockControls';
import LayerOrderControls from '../LayerOrderControls';
import SelectionCanvasAlignControls from '../SelectionCanvasAlignControls';
import { EditorContext } from '../../../../../../imageEditorComponent';
import { TEXT_TEMPLATE_PICKER_ID } from '../../../../../editorBridgeIds';
import { TOOLS_IDS } from '../../../utils/constants';
import isAnnotationOptionApplied, {
  canResetAnnotationOption,
  getAnnotationOptionResetPatch,
} from '../../../utils/isAnnotationOptionApplied';

const AnnotationOptions = ({
  children,
  morePoppableOptionsPrepended = [],
  moreOptionsPopupComponentsObj = {},
  morePoppableOptionsAppended = [],
  optionRowGroups = [],
  customOptionRowCells = {},
  annotation,
  updateAnnotation,
  hideFillOption = false,
  showTextStylesEntry = false,
  hidePositionField = false,
  fillRowCompanion = null,
  preFillRowChildren = null,
  className,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentOption, setCurrentOption] = useState(null);
  const {
    config: { useCloudimage },
    t,
  } = useStore();
  const { setCurrentId } = useContext(EditorContext);
  const isLocked = Boolean(annotation?.locked);
  const fillColorInputRef = useRef(null);
  const guardedUpdateAnnotation = useCallback(
    (patch) => {
      if (!isLocked) {
        updateAnnotation(patch);
      }
    },
    [isLocked, updateAnnotation],
  );
  const options = useMemo(
    () => [
      ...morePoppableOptionsPrepended,
      {
        titleKey: 'opacity',
        name: POPPABLE_OPTIONS.OPACITY,
        Icon: Transparency,
      },
      ...(!useCloudimage
        ? [
          { titleKey: 'stroke', name: POPPABLE_OPTIONS.STROKE, Icon: Stroke },
          { titleKey: 'shadow', name: POPPABLE_OPTIONS.SHADOW, Icon: Shadow },
        ]
        : []),
      !hidePositionField
        ? {
          titleKey: 'position',
          name: POPPABLE_OPTIONS.POSITION,
          Icon: Position,
        }
        : undefined,
    ],
    [morePoppableOptionsPrepended],
  );

  const optionsPopups = useMemo(
    () => ({
      ...moreOptionsPopupComponentsObj,
      [POPPABLE_OPTIONS.OPACITY]: OpacityField,
      [POPPABLE_OPTIONS.STROKE]: StrokeFields,
      [POPPABLE_OPTIONS.SHADOW]: ShadowFields,
      [POPPABLE_OPTIONS.POSITION]: PositionFields,
      ...morePoppableOptionsAppended,
    }),
    [moreOptionsPopupComponentsObj],
  );

  const closeOptionPopup = useCallback(() => {
    setAnchorEl(null);
    setCurrentOption(null);
  }, []);

  const openOptionPopup = useCallback((e, targetOptionName) => {
    setAnchorEl(e?.currentTarget ?? null);
    setCurrentOption(targetOptionName);
  }, []);

  const allPopupOptions = useMemo(
    () => options.filter(Boolean),
    [options],
  );

  const rowGroupByOptionName = useMemo(() => {
    const map = new Map();
    optionRowGroups.forEach((group) => {
      group.forEach((name) => {
        map.set(name, group);
      });
    });
    return map;
  }, [optionRowGroups]);

  const resolveRowCells = useCallback(
    (rowGroup) =>
      rowGroup
        .map((name) => {
          const option = allPopupOptions.find((item) => item.name === name);
          if (option) {
            return { kind: 'option', name, option };
          }
          const customNode = customOptionRowCells[name];
          if (customNode) {
            return { kind: 'custom', name, node: customNode };
          }
          return null;
        })
        .filter(Boolean),
    [allPopupOptions, customOptionRowCells],
  );

  const renderOptionButton = useCallback(
    (option) => (
      <SidebarPanelButton
        className="respark_annotation-option-triggerer text-style-icon"
        key={option.name}
        Icon={option.Icon}
        label={t(option.labelKey || option.titleKey)}
        title={t(option.titleKey)}
        disabled={isLocked}
        aria-selected={
          isAnnotationOptionApplied(annotation, option.name) ||
          (currentOption === option.name && Boolean(anchorEl))
        }
        onClick={(e) => openOptionPopup(e, option.name)}
      />
    ),
    [anchorEl, annotation, currentOption, isLocked, openOptionPopup, t],
  );

  const renderRowCell = useCallback(
    (cell) => (
      <div
        key={cell.name}
        className="respark_inspector-option-row-cell"
      >
        {cell.kind === 'option' ? renderOptionButton(cell.option) : cell.node}
      </div>
    ),
    [renderOptionButton],
  );

  const renderedOptionNodes = useMemo(() => {
    const renderedNames = new Set();
    const nodes = [];

    allPopupOptions.forEach((option) => {
      if (renderedNames.has(option.name)) {
        return;
      }

      const rowGroup = rowGroupByOptionName.get(option.name);
      if (rowGroup) {
        rowGroup.forEach((name) => renderedNames.add(name));
        const cells = resolveRowCells(rowGroup);

        if (cells.length === 0) {
          return;
        }
        if (cells.length === 1) {
          nodes.push(renderRowCell(cells[0]));
          return;
        }

        nodes.push(
          <div
            key={`row-${rowGroup.join('-')}`}
            className="respark_inspector-option-row"
          >
            {cells.map((cell) => renderRowCell(cell))}
          </div>,
        );
        return;
      }

      renderedNames.add(option.name);
      nodes.push(renderOptionButton(option));
    });

    return nodes;
  }, [
    allPopupOptions,
    renderOptionButton,
    renderRowCell,
    resolveRowCells,
    rowGroupByOptionName,
  ]);

  const currentOptionMeta = useMemo(
    () => allPopupOptions.find((option) => option.name === currentOption),
    [allPopupOptions, currentOption],
  );

  const resetCurrentOption = useCallback(() => {
    if (!currentOption || isLocked) {
      return;
    }
    const patch = getAnnotationOptionResetPatch(currentOption);
    if (patch) {
      guardedUpdateAnnotation(patch);
    }
  }, [currentOption, guardedUpdateAnnotation, isLocked]);

  const changeAnnotationFill = useCallback(
    (newFill) => {
      guardedUpdateAnnotation({ fill: newFill });
    },
    [guardedUpdateAnnotation],
  );

  const renderFillButton = useCallback(
    () => (
      <SidebarPanelButton
        className="respark_color-tile-wrap"
        label={t('fillColor')}
        title={t('fillColor')}
        disabled={isLocked}
        onClick={(event) => {
          if (!isLocked) {
            fillColorInputRef.current?.openPicker(event.currentTarget);
          }
        }}
        lead={(
          <span className="respark_color-tile-swatch">
            <ColorInput
              ref={fillColorInputRef}
              color={annotation.fill}
              onChange={changeAnnotationFill}
              triggerOpensPicker={false}
              disabled={isLocked}
            />
          </span>
        )}
      />
    ),
    [annotation.fill, changeAnnotationFill, isLocked, t],
  );

  const OptionPopupComponent =
    anchorEl && currentOption && optionsPopups[currentOption];

  const optionPopupMinWidth =
    currentOption === POPPABLE_OPTIONS.POSITION
      ? POSITION_POPUP_MIN_WIDTH
      : ANNOTATION_OPTION_POPUP_MIN_WIDTH;

  return (
    <StyledOptions
      className={`respark_annotations-options${className ? ` ${className}` : ''}${isLocked ? ' respark_annotations-options--locked' : ''}`}
      aria-disabled={isLocked || undefined}
    >
      <div className="respark_annotation-options-editable">
        <GroupLockControls showSectionTitle={false} />
        {showTextStylesEntry && (
          <SidebarPanelButton
            className="respark_text-styles-option respark_annotation-option-triggerer text-style-icon"
            Icon={TextIcon}
            label={t('textStylesShort')}
            title={t('textStylesTitle')}
            disabled={isLocked}
            onClick={() => setCurrentId(TEXT_TEMPLATE_PICKER_ID)}
          />
        )}
        {preFillRowChildren}
        {!hideFillOption && (
          fillRowCompanion ? (
            <div className="respark_inspector-option-row respark_fill-size-row">
              <div className="respark_inspector-option-row-cell">
                {renderFillButton()}
              </div>
              <div className="respark_inspector-option-row-cell">
                {fillRowCompanion}
              </div>
            </div>
          ) : (
            renderFillButton()
          )
        )}
        {children}
        <SelectionCanvasAlignControls />
        {renderedOptionNodes}
        {OptionPopupComponent && (
          <Menu
            className="respark_annotation-option-popup"
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && !isLocked}
            onClose={closeOptionPopup}
            position="top"
            containerProps={{
              style: {
                minWidth: optionPopupMinWidth,
                maxHeight: 'none',
                overflow: 'visible',
                overflowY: 'visible',
                overflowX: 'visible',
              },
            }}
          >
            <StyledOptionPopupContent className="respark_annotation-option-popup-content">
              {currentOptionMeta ? (
                <AnnotationOptionPanelHeader
                  title={t(currentOptionMeta.titleKey)}
                  closeLabel={t('annotationOptionClose')}
                  resetLabel={t('reset')}
                  canReset={
                    canResetAnnotationOption(currentOption) &&
                    isAnnotationOptionApplied(annotation, currentOption)
                  }
                  onClose={closeOptionPopup}
                  onReset={resetCurrentOption}
                />
              ) : null}
              <OptionPopupComponent
                annotation={annotation}
                updateAnnotation={guardedUpdateAnnotation}
                {...rest}
              />
            </StyledOptionPopupContent>
          </Menu>
        )}
      </div>
      <StyledAnnotationSidebarActions className="respark_annotation-sidebar-actions">
        <LayerOrderControls variant="sidebar" />
      </StyledAnnotationSidebarActions>
    </StyledOptions>
  );
};


AnnotationOptions.propTypes = {
  annotation: PropTypes.instanceOf(Object).isRequired,
  updateAnnotation: PropTypes.func.isRequired,
  children: PropTypes.node,
  hideFillOption: PropTypes.bool,
  showTextStylesEntry: PropTypes.bool,
  morePoppableOptionsPrepended: PropTypes.arrayOf(PropTypes.object),
  morePoppableOptionsAppended: PropTypes.arrayOf(PropTypes.object),
  optionRowGroups: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  customOptionRowCells: PropTypes.objectOf(PropTypes.node),
  moreOptionsPopupComponentsObj: PropTypes.instanceOf(Object),
  hidePositionField: PropTypes.bool,
  fillRowCompanion: PropTypes.node,
  preFillRowChildren: PropTypes.node,
  className: PropTypes.string,
};

export default AnnotationOptions;
